import { useState } from "react";
import {
    Button,
    Input,
    Typography,
    message,
    Space,
    Modal,
    Spin,
    Flex,
    Card,
} from "antd";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import { downloadBlob } from "./utils";
import GeneralUploader from "./Uploader";

const { Title, Text } = Typography;

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`;

export default function RemovePassword() {
    const [file, setFile] = useState(null);
    const [password, setPassword] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [unlockedBytes, setUnlockedBytes] = useState(null);
    const [unlockedFilename, setUnlockedFilename] = useState("");
    const [showUploader, setShowUploader] = useState(true);

    const resetAll = () => {
        setFile(null);
        setPassword("");
        setModalVisible(false);
        setUnlocking(false);
        setUnlockedBytes(null);
        setUnlockedFilename("");
        setShowUploader(true);
    };

    const getActualFile = (f) =>
        (Array.isArray(f) ? f[0] : f)?.originFileObj ??
        (Array.isArray(f) ? f[0] : f);

    const handleUploadChange = (info) => {
        const uploaded = Array.isArray(info) ? info[0] : info;
        if (!uploaded || uploaded.status === "uploading") return;

        const actual = uploaded.originFileObj ?? uploaded;
        if (!actual || typeof actual.arrayBuffer !== "function") {
            setFile(null);
            return;
        }

        setFile(actual);
        setShowUploader(false);
        setModalVisible(true);
    };

    const handleCancel = () => {
        if (unlocking) return;
        resetAll();
    };

    const handleUnlock = async () => {
        if (!file) return message.error("Please upload a PDF file");
        if (!password) return message.error("Please enter the password");

        setUnlocking(true);
        const key = "unlock";
        message.loading({ content: "Unlocking PDF...", key, duration: 0 });

        try {
            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = pdfjsLib.getDocument({
                data: arrayBuffer,
                password,
            });
            const pdf = await loadingTask.promise;

            const newDoc = await PDFDocument.create();

            for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale: 2 });

                const canvas = document.createElement("canvas");
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext("2d");

                await page.render({ canvasContext: ctx, viewport }).promise;

                const base64 = canvas.toDataURL("image/png").split(",")[1];
                const binary = Uint8Array.from(atob(base64), (c) =>
                    c.charCodeAt(0)
                );
                const img = await newDoc.embedPng(binary);

                const newPage = newDoc.addPage([
                    viewport.width,
                    viewport.height,
                ]);
                newPage.drawImage(img, {
                    x: 0,
                    y: 0,
                    width: viewport.width,
                    height: viewport.height,
                });
            }

            const bytes = await newDoc.save();
            setUnlockedBytes(bytes);
            setUnlockedFilename(
                `${file.name.replace(/\.pdf$/i, "")}_unlocked.pdf`
            );
            message.success({ content: "PDF unlocked successfully!", key });
            setModalVisible(false);
        } catch (err) {
            const errMsg =
                err?.message ||
                "Failed to unlock PDF. Ensure the password is correct and try again.";
            message.error({ content: `Error: ${errMsg}`, key: "unlock" });
        } finally {
            setUnlocking(false);
        }
    };

    const handleDownload = () => {
        if (!unlockedBytes) return;
        downloadBlob(unlockedBytes, unlockedFilename || "unlocked.pdf");
    };

    return (
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Flex
                vertical
                gap={40}
                align="center"
                style={{
                    padding: "32px 20px",
                }}
            >
                <Flex vertical align="center">
                    <Title level={1}>Unlock PDF File</Title>
                    <Text>
                        Remove the password from a PDF file (requires the
                        password)
                    </Text>
                </Flex>

                {showUploader && (
                    <GeneralUploader
                        type="pdf"
                        multiple={false}
                        accept="application/pdf"
                        showUploadList
                        beforeUpload={() => false}
                        onChange={handleUploadChange}
                    />
                )}

                {unlockedBytes && (
                    <Card
                        size="small"
                        style={{ padding: 20, textAlign: "center" }}
                    >
                        <Title level={3} style={{ margin: 0 }}>
                            Your file is ready
                        </Title>

                        <Flex vertical gap={14} style={{ marginTop: 20 }}>
                            <Button
                                type="primary"
                                size="default"
                                onClick={handleDownload}
                                style={{ minWidth: 220 }}
                            >
                                Download
                            </Button>
                            <Button
                                size="default"
                                onClick={resetAll}
                                style={{ minWidth: 220 }}
                            >
                                Start Over
                            </Button>
                        </Flex>
                    </Card>
                )}
            </Flex>

            <Modal
                title="Enter PDF Password"
                centered
                open={modalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button
                        key="cancel"
                        onClick={handleCancel}
                        disabled={unlocking}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key="unlock"
                        type="primary"
                        onClick={handleUnlock}
                        loading={unlocking}
                    >
                        Unlock
                    </Button>,
                ]}
                destroyOnHidden
                maskClosable={!unlocking}
                styles={{ body: { padding: 12 } }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <Input.Password
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onPressEnter={handleUnlock}
                        disabled={unlocking}
                    />

                    {unlocking && (
                        <Flex vertical align="center" style={{ paddingTop: 8 }}>
                            <Spin />
                            <div style={{ marginTop: 8 }}>Unlocking PDF...</div>
                        </Flex>
                    )}
                </Space>
            </Modal>
        </div>
    );
}
